import React, { useCallback, useEffect, useMemo, useState } from "react";
import "../JobPosting/jobs.scss";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import Select from "react-select";
import {
  fetchJobEmailSettings,
  updateJobEmailSettings,
} from "../../Redux/ActionCreators/jobsAction";
import { useJobSettingsAccess } from "../JobPosting/JobSettingsAccessContext";

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    borderRadius: 8,
    fontSize: 14,
    fontFamily: '"inter", sans-serif',
    borderColor: state.isFocused ? "#f75891" : "#e4e9f0",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(247, 88, 145, 0.12)" : "none",
    "&:hover": { borderColor: state.isFocused ? "#f75891" : "#f9a8d4" },
  }),
  placeholder: (base) => ({ ...base, color: "#8a9199" }),
  singleValue: (base) => ({ ...base, color: "#111928" }),
  multiValue: (base) => ({ ...base, backgroundColor: "#fce7f1", borderRadius: 6 }),
  multiValueLabel: (base) => ({ ...base, color: "#111928", fontSize: 13 }),
  multiValueRemove: (base) => ({
    ...base,
    color: "#f75891",
    "&:hover": { backgroundColor: "#f9a8d4", color: "#fff" },
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 8,
    overflow: "hidden",
    boxShadow: "0 8px 24px rgba(17, 25, 40, 0.12)",
  }),
  option: (base, state) => ({
    ...base,
    padding: "8px 12px",
    cursor: "pointer",
    fontFamily: '"inter", sans-serif',
    backgroundColor: state.isSelected ? "#fce7f1" : state.isFocused ? "#fff7fb" : "#fff",
    color: "#111928",
  }),
};

const toOption = (u) => ({
  value: u.id,
  label: u.name ? `${u.name} (${u.email})` : u.email,
  email: u.email,
  name: u.name,
});

const labelForUser = (usersById, id) => {
  const u = usersById[id];
  if (!u) return null;
  return u.name ? `${u.name} (${u.email})` : u.email;
};

const JobEmailSettings = ({ setType, setSnackbar }) => {
  const { readOnly } = useJobSettingsAccess();
  const { jobId } = useParams();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [replyTo, setReplyTo] = useState(null);
  const [ccUsers, setCcUsers] = useState([]);
  const [inheritsTenant, setInheritsTenant] = useState(true);
  const [tenantDefaults, setTenantDefaults] = useState({
    reply_to_user_id: null,
    cc_user_ids: [],
  });

  const userOptions = useMemo(() => users.map(toOption), [users]);
  const usersById = useMemo(
    () => Object.fromEntries(users.map((u) => [u.id, u])),
    [users]
  );

  const applyData = useCallback((data) => {
    const list = Array.isArray(data?.available_users) ? data.available_users : [];
    setUsers(list);
    const byId = Object.fromEntries(list.map((u) => [u.id, u]));
    const replyId = data?.reply_to_user_id;
    setReplyTo(replyId && byId[replyId] ? toOption(byId[replyId]) : null);
    const ccIds = Array.isArray(data?.cc_user_ids) ? data.cc_user_ids : [];
    setCcUsers(ccIds.map((id) => byId[id]).filter(Boolean).map(toOption));
    setInheritsTenant(!!data?.inherits_tenant);
    setTenantDefaults({
      reply_to_user_id: data?.tenant_defaults?.reply_to_user_id ?? null,
      cc_user_ids: Array.isArray(data?.tenant_defaults?.cc_user_ids)
        ? data.tenant_defaults.cc_user_ids
        : [],
    });
  }, []);

  const load = useCallback(async () => {
    if (!jobId) return;
    setLoading(true);
    const res = await dispatch(fetchJobEmailSettings(jobId));
    if (res.success) {
      applyData(res.data);
    } else {
      setSnackbar?.({
        open: true,
        message: res.message || "Failed to load email settings",
        severity: "error",
      });
    }
    setLoading(false);
  }, [dispatch, jobId, applyData, setSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  const replyPlaceholder = useMemo(() => {
    if (!inheritsTenant) return "No Reply-To for this job";
    const label = labelForUser(usersById, tenantDefaults.reply_to_user_id);
    return label ? `Company default: ${label}` : "Inherit company default";
  }, [inheritsTenant, usersById, tenantDefaults.reply_to_user_id]);

  const notify = (message, severity = "error") => {
    setSnackbar?.({ open: true, message, severity });
  };

  const handleSave = async () => {
    if (readOnly) return;
    setSaving(true);
    const res = await dispatch(
      updateJobEmailSettings(jobId, {
        reply_to_user_id: replyTo?.value ?? null,
        cc_user_ids: ccUsers.map((o) => o.value),
        clear: false,
      })
    );
    setSaving(false);
    if (res.success) {
      applyData(res.data);
      notify("Email settings saved", "success");
      setType("");
    } else {
      notify(res.message || "Failed to save");
    }
  };

  const handleClear = async () => {
    if (readOnly) return;
    setSaving(true);
    const res = await dispatch(updateJobEmailSettings(jobId, { clear: true }));
    setSaving(false);
    if (res.success) {
      applyData(res.data);
      notify("Cleared — using company defaults", "success");
    } else {
      notify(res.message || "Failed to clear");
    }
  };

  const tenantCcHint = useMemo(() => {
    if (!inheritsTenant) {
      return "Cleared CC means no CC on this job (does not fall back to company defaults). Use “Clear to use company defaults” to inherit.";
    }
    const labels = (tenantDefaults.cc_user_ids || [])
      .map((id) => labelForUser(usersById, id))
      .filter(Boolean);
    if (!labels.length) return "Empty inherits company CC list until you save an override.";
    return `Company default CC: ${labels.join(", ")}`;
  }, [inheritsTenant, tenantDefaults.cc_user_ids, usersById]);

  return (
    <div className="job-prescreening">
      <div className="job-settings-head">
        <img
          src="/images/back-arrow-black.svg"
          alt="back"
          width={32}
          height={32}
          onClick={() => setType("")}
          style={{ cursor: "pointer" }}
        />
        <div className="job-settings-head-title">
          <h3>Email Settings</h3>
        </div>
      </div>

      <div className="job-settings-body">
        {loading ? (
          <div
            className="loading-overlay"
            style={{
              position: "relative",
              marginTop: 16,
              alignItems: "start",
              height: "32px",
            }}
          >
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <div className="job-settings-form" style={{ maxWidth: 560 }}>
            <p style={{ fontSize: 13, color: "#525b66", marginBottom: 16 }}>
              {inheritsTenant
                ? "Currently using company defaults. Set values below to override for this job."
                : "This job overrides company email defaults."}{" "}
              Candidate emails also CC the assigned recruiter when ownership comes from Round Robin or a recruiter apply link.
            </p>

            <div style={{ marginBottom: 20 }}>
              <label
                style={{
                  display: "block",
                  fontFamily: "inter, sans-serif",
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#1A1E23",
                  marginBottom: 8,
                }}
              >
                Reply-To
              </label>
              <Select
                isClearable
                isDisabled={readOnly}
                options={userOptions}
                value={replyTo}
                onChange={setReplyTo}
                placeholder={replyPlaceholder}
                styles={selectStyles}
              />
            </div>

            <div style={{ marginBottom: 8 }}>
              <label
                style={{
                  display: "block",
                  fontFamily: "inter, sans-serif",
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#1A1E23",
                  marginBottom: 8,
                }}
              >
                CC
              </label>
              <Select
                isMulti
                isClearable
                isDisabled={readOnly}
                options={userOptions}
                value={ccUsers}
                onChange={(v) => setCcUsers(v || [])}
                placeholder="Select users to CC…"
                closeMenuOnSelect={false}
                styles={selectStyles}
              />
              <p style={{ fontSize: 12, color: "#8a9199", marginTop: 8 }}>{tenantCcHint}</p>
            </div>

            {!readOnly && (
              <div style={{ display: "flex", gap: 12, marginTop: 20, flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  style={{
                    border: "none",
                    backgroundColor: "#f75891",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: 14,
                    borderRadius: 4,
                    height: 36,
                    padding: "0 20px",
                    opacity: saving ? 0.7 : 1,
                  }}
                >
                  {saving ? "Saving…" : "Save"}
                </button>
                <button
                  type="button"
                  onClick={handleClear}
                  disabled={saving || inheritsTenant}
                  style={{
                    border: "1px solid #e4e9f0",
                    backgroundColor: "#fff",
                    color: "#525b66",
                    fontWeight: 600,
                    fontSize: 14,
                    borderRadius: 4,
                    height: 36,
                    padding: "0 16px",
                    opacity: inheritsTenant ? 0.5 : 1,
                  }}
                >
                  Clear to use company defaults
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default JobEmailSettings;
